import {Component, inject, OnInit} from '@angular/core';
import {IonicModule, LoadingController, Platform} from '@ionic/angular';
import {NavigationEnd, Router, RouterModule} from '@angular/router';
import {EMPTY, filter, forkJoin, merge, take, timer} from 'rxjs';
import {CupertinoPane} from 'cupertino-pane';
import {Network} from '@capacitor/network';
import {Browser} from '@capacitor/browser';
import {UpdateService} from './services/update.service';
import {StorageService} from './services/storage.service';
import {DealService} from './services/deal.service';
import {AuthService} from './services/auth.service';
import {IUser} from './interfaces/IUser';
import {BlogService} from './services/blog.service';
import {ThemeService} from './services/theme.service';
import {UtilityService} from './services/utility.service';
import {environment} from '../environments/environment';
import {UTILITY} from './components/utility/utility.component';
import {FormsModule} from '@angular/forms';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {CommonModule} from '@angular/common';
import {catchError, map, tap} from 'rxjs/operators';
import {Device} from '@capacitor/device';
import {AppStatusBarService} from './services/app-status-bar.service';
import {register} from 'swiper/element/bundle';

register();

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        IonicModule,
        RouterModule,
        FormsModule,
        FontAwesomeModule
    ]
})
export class AppComponent implements OnInit {
    private splashHidden = false; // ✅ evita hide multiple

    // Iniezioni
    private readonly router = inject(Router);
    private readonly themeService = inject(ThemeService);
    private readonly utilityService = inject(UtilityService);
    private readonly platform = inject(Platform);
    private readonly blogService = inject(BlogService);
    private readonly ds = inject(DealService);
    private readonly storage = inject(StorageService);
    private readonly updateService = inject(UpdateService);
    private readonly authService = inject(AuthService);
    private readonly loadingController = inject(LoadingController);
    private readonly status = inject(AppStatusBarService);

    // Stato
    readyLunch = false;
    paneBulletinErrorDeals: CupertinoPane;
    paneBulletinRateApp: CupertinoPane;
    switchingPage = false;
    navigate: any;
    flagConnection = true;
    currentUser: IUser = null;

    // Loading globale
    private loadingInstance: HTMLIonLoadingElement | null = null;
    private showLoading = false;

    constructor() {
    }

    ngOnInit(): void {
        console.log('#### VERSIONE APP: 1.0.11');
        this.themeService.init();
        this.initializeApp();

        // Loading reattivo
        this.utilityService.loading.subscribe(value => {
            if (value > 0 && !this.showLoading) {
                this.showLoading = true;
                this.createLoading().then(() => this.checkingLoading());
            } else if (value === 0 && this.showLoading) {
                this.showLoading = false;
                this.dismissLoading();
            } else if (value < 0) {
                this.utilityService.valueOfLoading(1);
            }
        });

        // Esecuzioni asincrone
        (async () => {
            this.platform.pause.subscribe(() => {
                this.handlePauseEvent().catch(err => console.error('Errore in pause:', err));
            });

            this.platform.resume.subscribe(() => {
                this.handleResumeEvent().catch(err => console.error('Errore in resume:', err));
            });

            this.authService.user.subscribe((user: IUser) => {
                this.currentUser = user;
                if (this.currentUser?.emailVerified) {
                    const dealsList = this.ds.dealsList.getValue();
                    if (dealsList?.length > 0) {
                        this.setLikedList();
                    }
                    if (this.currentUser.userLevel > 3 && this.themeService.typeDevice !== 0) {
                        this.handleAdminSubscription();
                    }
                } else {
                    this.resetLikedList();
                }
            });
        })().catch(err => console.error('Errore nell’IIFE di ngOnInit:', err));
    }

    // --- Pause/Resume --------------------------------------------------------

    private async handlePauseEvent(): Promise<void> {
        const key = 'lastDatePause' + environment.storageVersion;
        const storedValue = await this.storage.get(key);
        if (UTILITY.checkObj(storedValue)) {
            await this.storage.remove(key);
        }
        await this.storage.set(key, Date.now());
    }

    private async handleResumeEvent(): Promise<void> {
        const key = 'lastDatePause' + environment.storageVersion;
        const date = await this.storage.get(key);
        if (date && (Date.now() - date >= 600000) && !this.ds.openFromNotification) {
            await this.router.navigate(['/home/deals-list']);
            this.reloadAllList();
        }
    }

    private handleAdminSubscription(): void {
        const adminKey = 'adminNotification' + environment.storageVersion;
        this.storage.get(adminKey)
            .then(check => {
                if (!UTILITY.checkObj(check) || !check) {
                    this.ds.subscribeTo('admin', null, false);
                    return this.storage.set(adminKey, true);
                }
            })
            .catch(err => console.error('Errore nella gestione admin:', err));
    }

    // --- Fetch liste ---------------------------------------------------------

    reloadAllList() {
        merge(
            this.ds.getAllInEvidences().pipe(
                tap(inEvidenceList => this.ds.inEvidenceList.next(inEvidenceList))
            ),
            this.ds.getAllDeals().pipe(
                tap(deals => this.ds.dealsList.next(deals)),
                catchError(error => {
                    this.presentBulletinErrorDeals();
                    return EMPTY;
                })
            ),
            this.blogService.getAllTutorials().pipe(
                tap(tutorials => this.blogService.tutorialsList.next(tutorials))
            )
        ).subscribe();
    }

    // --- Bootstrap app -------------------------------------------------------

    initializeApp() {
        this.platform.ready().then(async () => {
            // Device info e popup
            this.setDeviceInfo();
            this.checkAndPresentPopUp();

            // Aggiornamenti
            if (this.themeService.notWeb) {
                this.updateService.checkForUpdate();
            }

            // Network
            Network.addListener('networkStatusChange', status => {
                this.flagConnection = status.connected;
            });

            // Calcolo dimensioni UI
            let heightImage = 200;
            let heightColumn = 230;
            let approxItemHeight = 250;
            if (this.platform.width() < 470) {
                heightImage = this.platform.width() * 0.2722;
                heightColumn = this.platform.width() * 0.3467;
                approxItemHeight = this.platform.width() * 0.4964;
            }
            this.ds.heightColumn.next(heightColumn);
            this.ds.maxImageHeight.next(heightImage);
            this.ds.approxItemHeight.next(approxItemHeight);
            if (this.themeService.typeDevice === 2) {
                this.ds.approxItemHeightPostBlog.next(((this.platform.width() - 32) * 0.3623) + 53);
            } else {
                this.ds.approxItemHeightPostBlog.next(((this.platform.width() - 20) * 0.3623) + 53);
            }

            // Push
            if (this.themeService.notWeb) {
                // colore coerente anche all’avvio
                await this.status.init();
                this.ds.initPush();

                // ✅ Triggers compatti e sicuri (StatusBar + Splash)
                const nav$ = this.router.events.pipe(
                    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                    // url normalizzato e valido
                    map(e => (e.urlAfterRedirects || (e as any).url || '').toLowerCase()),
                    filter(url => UTILITY.checkText(url))
                );

                // Applica la StatusBar solo quando cambia "contesto" (es. deals-list vs altro)
                /**
                 nav$.pipe(
                 map(url => url.includes('deals-list')),
                 distinctUntilChanged()
                 ).subscribe(lightBg => {
                 void this.status.apply(lightBg); // evita chiamate ripetute inutili
                 });
                 */
                // Nascondi Splash: prima nav utile O, in fallback, dopo 6s (vince il primo che arriva)
                merge(
                    nav$.pipe(take(1)),
                    timer(6000).pipe(take(1))
                ).subscribe(() => this.hideSplashSafely());

            }

            // Fetch iniziale
            forkJoin([
                this.ds.getAllInEvidences(),
                this.ds.getAllDeals()
            ]).subscribe(res => {
                this.ds.inEvidenceList.next(res[0]);
                this.ds.dealsList.next(res[1]);
                this.readyLunch = true;
                this.status.applyBlueWhite();
                // Manteniamo anche la tua hide qui:
                this.utilityService.valueOfSplashScreen().then(() => {
                });
            });

            forkJoin([
                this.blogService.getAllCommunications(true),
                this.blogService.getAllTutorials(true),
                this.ds.getAllNotifications(true),
                this.ds.listTags(true)
            ]).subscribe(res => {
                this.blogService.communicationsList.next(res[0]);
                this.blogService.tutorialsList.next(res[1]);
                this.ds.notificationsList.next(res[2]);
                this.ds.tags = res[3];
            });

            this.authService.initializeSocialLogin();
        });
    }

    // --- Loading -------------------------------------------------------------

    async createLoading(): Promise<void> {
        if (!this.showLoading) {
            this.showLoading = true;
            this.loadingInstance = await this.loadingController.create({
                cssClass: 'my-loading-class',
                message: 'Caricamento...',
            });
            await this.loadingInstance.present();
            this.checkingLoading();
        }
    }

    dismissLoading(): void {
        if (this.loadingInstance) {
            this.loadingInstance.dismiss();
            this.loadingInstance = null;
            this.showLoading = false;
        }
    }

    checkingLoading(): void {
        setTimeout(() => {
            this.showLoading = false;
            this.dismissLoading();
        }, 10000);
    }

    // --- Likes ---------------------------------------------------------------

    setLikedList(): void {
        this.blogService.getAllUserLikes().subscribe(userLikesList => {
            this.ds.userLikesList.next(userLikesList);
            if (userLikesList.dealsObjLikes.length > 0) {
                const dealsList = this.ds.dealsList.getValue();
                dealsList.forEach(deal => {
                    userLikesList.dealsObjLikes.forEach(dealLiked => {
                        if (deal.id === dealLiked.id) {
                            deal.liked = true;
                        }
                    });
                });
                this.ds.dealsList.next(dealsList);
            }
            if (userLikesList.communicationsIdLikes.length > 0) {
                const communicationsList = this.blogService.communicationsList.getValue();
                communicationsList.forEach(item => {
                    userLikesList.communicationsIdLikes.forEach(id => {
                        if (item.id === id) {
                            item.liked = true;
                        }
                    });
                });
                this.blogService.communicationsList.next(communicationsList);
            }
            if (userLikesList.tutorialsIdLikes.length > 0) {
                const tutorialsList = this.blogService.tutorialsList.getValue();
                tutorialsList.forEach(item => {
                    userLikesList.tutorialsIdLikes.forEach(id => {
                        if (item.id === id) {
                            item.liked = true;
                        }
                    });
                });
                this.blogService.tutorialsList.next(tutorialsList);
            }
        });
    }

    resetLikedList(): void {
        this.ds.userLikesList.next(null);
        const dealsList = this.ds.dealsList.getValue();
        dealsList.forEach(deal => {
            deal.liked = false;
        });
        this.ds.dealsList.next(dealsList);
        const communicationsList = this.blogService.communicationsList.getValue();
        communicationsList.forEach(item => {
            item.liked = false;
        });
        this.blogService.communicationsList.next(communicationsList);
        const tutorialsList = this.blogService.tutorialsList.getValue();
        tutorialsList.forEach(item => {
            item.liked = false;
        });
        this.blogService.tutorialsList.next(tutorialsList);
    }

    // --- Varie ---------------------------------------------------------------

    closeFlagNotification(): void {
        this.flagConnection = true;
    }

    async presentBulletinErrorDeals(): Promise<void> {
        this.paneBulletinErrorDeals = new CupertinoPane('.paneBulletinErrorDeals', {
            parentElement: 'body',
            fitHeight: true,
            backdrop: true,
            topperOverflow: false,
            backdropOpacity: 0.6,
            bottomClose: true,
            showDraggable: false,
            buttonDestroy: true,
            upperThanTop: true,
            bottomOffset: 20,
            cssClass: 'styleBulletin'
        });
        await this.paneBulletinErrorDeals.present({animate: true});
    }

    async checkAndPresentPopUp(): Promise<void> {
        const stopCheck = await this.storage.get('stopCheck');
        if (!stopCheck) {
            const firstLaunchDate = await this.storage.get('firstLaunchDate');
            if (!firstLaunchDate) {
                await this.storage.set('firstLaunchDate', new Date().toISOString());
            } else {
                const daysSinceFirstLaunch = this.calculateDaysSinceFirstLaunch(firstLaunchDate);
                if (daysSinceFirstLaunch >= 7) {
                    setTimeout(() => {
                        this.presentBulletinRateApp();
                    }, 5000);
                }
            }
        }
    }

    private calculateDaysSinceFirstLaunch(firstLaunchDate: string): number {
        const oneDay = 24 * 60 * 60 * 1000;
        const today = new Date();
        const launchDate = new Date(firstLaunchDate);
        return Math.round(Math.abs((today.getTime() - launchDate.getTime()) / oneDay));
    }

    async presentBulletinRateApp(): Promise<void> {
        this.paneBulletinRateApp = new CupertinoPane('.paneBulletinRateApp', {
            parentElement: 'body',
            fitHeight: true,
            backdrop: true,
            topperOverflow: false,
            backdropOpacity: 0.6,
            bottomClose: true,
            showDraggable: false,
            buttonDestroy: false,
            upperThanTop: true,
            bottomOffset: 20,
            cssClass: 'styleBulletinRateApp'
        });
        await this.paneBulletinRateApp.present({animate: true});
    }

    resetRateAppMemory(): void {
        this.paneBulletinRateApp.destroy({animate: true}).then(() => {
            this.storage.remove('firstLaunchDate');
        });
    }

    rateApp(shouldRate: boolean): void {
        this.paneBulletinRateApp.destroy({animate: true}).then(() => {
            this.storage.set('stopCheck', 'true');
            if (shouldRate) {
                if (this.platform.is('android')) {
                    Browser.open({url: 'https://play.google.com/store/apps/details?id=com.gosumania.app'});
                } else {
                    Browser.open({url: 'https://apps.apple.com/it/app/promomania/id1567611977'});
                }
            }
        });
    }

    async setDeviceInfo() {
        const device = await Device.getInfo();
        this.utilityService.deviceInfoMessage = device.platform + ' - ' + device.osVersion;
    }

    // ✅ hide idempotente, richiamabile da più punti senza effetti collaterali
    private async hideSplashSafely(): Promise<void> {
        if (this.splashHidden) {
            return;
        }
        this.splashHidden = true;
        try {
            await this.utilityService.valueOfSplashScreen();
        } catch (error) {
            console.error('Errore nel nascondere la SplashScreen:', error);
        }
    }
}
